import { inject, Injectable, InjectionToken } from '@angular/core';

export type RealtimeRetryDelay = (
  milliseconds: number,
  signal: AbortSignal,
) => Promise<void>;

const RETRY_DELAYS_MILLISECONDS = [2_000, 4_000, 8_000, 16_000, 30_000] as const;
const ATTEMPT_WINDOW_MILLISECONDS = 120_000;
const MAX_ATTEMPTS_PER_WINDOW = 10;

export const REALTIME_RETRY_NOW = new InjectionToken<() => number>('REALTIME_RETRY_NOW', {
  factory: () => Date.now,
});

export const REALTIME_RETRY_DELAY = new InjectionToken<RealtimeRetryDelay>(
  'REALTIME_RETRY_DELAY',
  { factory: () => cancellableDelay },
);

@Injectable()
export class RealtimeRetryPolicy {
  private readonly now = inject(REALTIME_RETRY_NOW);
  private readonly delay = inject(REALTIME_RETRY_DELAY);

  private delayIndex = 0;
  private readonly admittedAttempts: number[] = [];

  admitAttempt(): boolean {
    const now = this.now();
    const windowStart = now - ATTEMPT_WINDOW_MILLISECONDS;
    while (
      this.admittedAttempts.length > 0
      && this.admittedAttempts[0] <= windowStart
    ) {
      this.admittedAttempts.shift();
    }
    if (this.admittedAttempts.length >= MAX_ATTEMPTS_PER_WINDOW) return false;

    this.admittedAttempts.push(now);
    return true;
  }

  async waitForRetry(signal: AbortSignal): Promise<boolean> {
    const delayMilliseconds = RETRY_DELAYS_MILLISECONDS[
      Math.min(this.delayIndex, RETRY_DELAYS_MILLISECONDS.length - 1)
    ];
    this.delayIndex += 1;
    await this.delay(delayMilliseconds, signal);
    return this.admitAttempt();
  }

  resetDelayProgression(): void {
    this.delayIndex = 0;
  }
}

function cancellableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortReason(signal));
      return;
    }

    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      clearTimeout(timeout);
      reject(abortReason(signal));
    };
    signal.addEventListener('abort', abort, { once: true });
  });
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Retry delay aborted.', 'AbortError');
}